import type { GeojsonCoordinate } from '/@/types/map'
import type { WaylinePreviewModel } from '/@/utils/parse-wayline-kmz'
import { wgs84togcj02 } from '/@/vendors/coordtransform'

const WAYLINE_STROKE = '#1677ff'
const WAYLINE_FILL_OPACITY = 0.12

function toGcj (c: GeojsonCoordinate): GeojsonCoordinate {
  return wgs84togcj02(c[0], c[1]) as GeojsonCoordinate
}

function toLngLat (AMap: any, c: GeojsonCoordinate) {
  return new AMap.LngLat(c[0], c[1])
}

function createCircleMarker (AMap: any, opts: Record<string, unknown>) {
  if (typeof AMap.CircleMarker === 'function') {
    return new AMap.CircleMarker(opts)
  }
  return new AMap.Circle(opts)
}

function dedupeConsecutive (pts: GeojsonCoordinate[]): GeojsonCoordinate[] {
  if (pts.length <= 1) {
    return pts
  }
  const res: GeojsonCoordinate[] = [pts[0]]
  for (let i = 1; i < pts.length; i++) {
    const a = res[res.length - 1]
    const b = pts[i]
    if (a[0] !== b[0] || a[1] !== b[1]) {
      res.push(b)
    }
  }
  return res
}

/** Resolve full route polylines to draw (prefer dense LineString over sparse waypoints). */
export function resolveWaylineRoutePaths (model: WaylinePreviewModel): GeojsonCoordinate[][] {
  const denseLines = (model.linePaths ?? [])
    .map(line => dedupeConsecutive(line))
    .filter(line => line.length >= 2)
  if (denseLines.length) {
    return denseLines
  }

  if (model.path.length >= 2) {
    return [dedupeConsecutive(model.path)]
  }

  if (model.waypoints.length >= 2) {
    return [dedupeConsecutive(model.waypoints.map(w => w.coord))]
  }

  if (model.takeoff && model.rth) {
    return [[model.takeoff.coord, model.rth.coord]]
  }

  return []
}

function drawPolyline (map: any, AMap: any, path: GeojsonCoordinate[], zIndex: number) {
  const gcjPath = path.map(toGcj).map(c => toLngLat(AMap, c))
  const polyline = new AMap.Polyline({
    path: gcjPath,
    strokeColor: WAYLINE_STROKE,
    strokeOpacity: 1,
    strokeWeight: 3,
    strokeStyle: 'solid',
    zIndex,
  })
  map.add(polyline)
  return polyline
}

/** Draw wayline KMZ preview on a standalone AMap instance; returns overlays for cleanup. */
export function drawWaylineOverlays (map: any, AMap: any, model: WaylinePreviewModel): any[] {
  if (!map || !AMap) {
    return []
  }
  const overlays: any[] = []

  model.polygons.forEach(poly => {
    const ring = poly.ring.map(toGcj)
    if (ring.length < 3) {
      return
    }
    const polygon = new AMap.Polygon({
      path: ring.map(c => toLngLat(AMap, c)),
      strokeColor: WAYLINE_STROKE,
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: WAYLINE_STROKE,
      fillOpacity: WAYLINE_FILL_OPACITY,
      zIndex: 40,
    })
    map.add(polygon)
    overlays.push(polygon)
  })

  const routePaths = resolveWaylineRoutePaths(model)
  routePaths.forEach((path, idx) => {
    overlays.push(drawPolyline(map, AMap, path, 50 + idx))
  })

  drawWaylineTakeoffRthMarkers(map, AMap, model, overlays)

  return overlays
}

/** 仅绘制起飞/返航点（不绘制航点/拍摄点标记）。 */
function drawWaylineTakeoffRthMarkers (
  map: any,
  AMap: any,
  model: WaylinePreviewModel,
  overlays: any[]
) {
  if (model.takeoff) {
    const c = toGcj(model.takeoff.coord)
    const takeoff = createCircleMarker(AMap, {
      center: [c[0], c[1]],
      radius: 8,
      fillColor: '#19be6b',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      zIndex: 95,
    })
    map.add(takeoff)
    overlays.push(takeoff)
  }

  if (model.rth) {
    const c = toGcj(model.rth.coord)
    const rth = createCircleMarker(AMap, {
      center: [c[0], c[1]],
      radius: 8,
      fillColor: '#e23c39',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      zIndex: 95,
    })
    map.add(rth)
    overlays.push(rth)
  }
}

export function clearWaylineOverlays (map: any, overlays: any[]) {
  if (!map || !overlays.length) {
    return
  }
  try {
    map.remove(overlays)
  } catch {
    // ignore
  }
}

export function hasWaylineGeometry (model: WaylinePreviewModel | null | undefined): boolean {
  if (!model) {
    return false
  }
  if (resolveWaylineRoutePaths(model).length > 0) {
    return true
  }
  return model.polygons.length > 0 || !!model.takeoff || !!model.rth
}
